import type { MonitoredProfile } from "../domain/types";
import { t } from "../i18n";
import { formatRelativeTime } from "../lib/time";
import { Lock, Trash2, UsersRound } from "lucide-react";

interface MonitoringStatusCardProps {
  profile: MonitoredProfile;
  onClick?: () => void;
  onDelete?: () => void;
}

const statusConfig: Record<string, { labelKey: string; tone: string }> = {
  ACTIVE: { labelKey: "monitoringActive", tone: "success" },
  PAUSED_AUTO: { labelKey: "monitoringPaused", tone: "warning" },
  PAUSED_MANUAL: { labelKey: "monitoringPaused", tone: "warning" },
  DISABLED: { labelKey: "monitoringDisabled", tone: "muted" },
  RATE_LIMITED: { labelKey: "monitoringRateLimited", tone: "warning" },
  PRIVATE: { labelKey: "monitoringPrivate", tone: "private" },
  RESTRICTED: { labelKey: "monitoringError", tone: "danger" },
  BUSINESS: { labelKey: "monitoringActive", tone: "info" },
};

export function MonitoringStatusCard({ profile, onClick, onDelete }: MonitoringStatusCardProps) {
  const config = statusConfig[profile.status] ?? statusConfig.ACTIVE;
  const updatedAt = profile.lastScrapedAt
    ? formatRelativeTime(profile.lastScrapedAt)
    : null;

  return (
    <div
      onClick={onClick}
      className={`app-profile-card ${onClick ? "is-clickable" : ""}`}
    >
      <div className="app-profile-card-main">
        <div className="app-avatar">
          {profile.avatarUrl ? (
            <img src={profile.avatarUrl} alt="" />
          ) : (
            profile.displayName?.charAt(0) ?? profile.handle.charAt(0).toUpperCase()
          )}
        </div>

        <div className="app-profile-card-copy">
          <div className="app-profile-card-title-row">
            <h3>@{profile.handle}</h3>
            {profile.isPrivate && (
              <span className="app-private-chip"><Lock size={12} /> {t("monitoringPrivate")}</span>
            )}
          </div>
          <p className="app-profile-following-line">
            <UsersRound size={16} />
            {profile.followingCount.toLocaleString()} {t("commonFollowing")}
            {(profile.newFollowsCount ?? 0) > 0 && <strong className="app-delta-positive">+ {profile.newFollowsCount}</strong>}
            {(profile.unfollowsCount ?? 0) > 0 && <strong className="app-delta-negative">- {profile.unfollowsCount}</strong>}
          </p>
          {updatedAt && <small>{t("profileUpdatedAt", { time: updatedAt })}</small>}
        </div>

        <div className={`app-status-pill app-status-${config.tone}`}>
          {t(config.labelKey)}
        </div>
      </div>

      {onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="app-icon-button app-profile-remove"
          title={t("profileDetailRemoveBtn")}
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );
}
